import { useEffect, useState } from "react";
import { AlertTriangle, HardDriveUpload, PlugZap, RefreshCw, Save, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const defaults = {
  protocol: "sftp",
  host: "",
  port: 22,
  username: "",
  password: "",
  remote_dir: "/gravacoes",
  keep_local_days: 1,
  keep_remote_days: 30,
  enabled: false,
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return String(value);
  }
};

function StatusTag({ status, title }) {
  if (status === "sent") return <span className="sbc-tag tag-online">enviado</span>;
  if (status === "pending") return <span className="sbc-tag tag-warning">pendente</span>;
  if (status === "purged") return <span className="sbc-tag">expurgado</span>;
  return <span className="sbc-tag tag-offline" title={title || ""}>erro</span>;
}

export default function Recordings() {
  const [form, setForm] = useState(defaults);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [retention, setRetention] = useState({});

  const load = async () => {
    const [configResponse, transferResponse, retentionResponse] = await Promise.all([
      api.get("/recording-storage"),
      api.get("/recording-transfers"),
      api.get("/recordings/retention/status"),
    ]);
    setForm({ ...defaults, ...configResponse.data, password: "" });
    setRows(transferResponse.data || []);
    setRetention(retentionResponse.data || {});
  };

  useEffect(() => {
    load().catch(() => setError("Não foi possível carregar as configurações."));
  }, []);

  const change = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const protocol = (value) => setForm((old) => ({ ...old, protocol: value, port: value === "sftp" ? 22 : 21 }));

  const save = async () => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await api.put("/recording-storage", form);
      setMessage("Configuração salva e vinculada ao cliente selecionado.");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const { data } = await api.post("/recording-storage/test");
      setMessage(data.detail);
    } catch (e) {
      setError(e.response?.data?.detail || "Falha no teste de conexão.");
    } finally {
      setBusy(false);
    }
  };

  const runRetention = async () => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const { data } = await api.post("/recordings/retention/run");
      setMessage(`Processamento concluído: ${data.sent} enviadas, ${data.purged} apagadas, ${data.errors} erros.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Falha ao processar gravações.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="recordings-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Armazenamento externo</div>
          <h1 className="text-2xl font-semibold tracking-tight">Gravações e verificação</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">
            Cliente em foco: <span className="font-semibold">{retention.tenant_name || "Não identificado"}</span>
          </p>
        </div>
        <button className="sbc-btn" onClick={load} disabled={busy} title="Atualizar dados">
          <RefreshCw size={14} />
        </button>
      </div>

      {message && <div className="text-xs text-[color:var(--accent-green)] font-mono">{message}</div>}
      {error && <div className="text-xs text-[color:var(--accent-red)] font-mono">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Retenção do cliente</div>
          <div className="text-2xl font-mono font-semibold">{retention.retention_days || 30} dias</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Retenção externa</div>
          <div className="text-2xl font-mono font-semibold">{retention.external_retention_days || 30} dias</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Último processamento</div>
          <div className="text-xs font-mono mt-2">{formatDateTime(retention.last_run)}</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Exportadas</div>
          <div className="text-2xl font-mono font-semibold">{retention.sent_total || 0}</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Pendentes / erro</div>
          <div className="text-2xl font-mono font-semibold">{retention.pending_or_error || 0}</div>
        </div>
        <div className="sbc-card p-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase text-[color:var(--text-muted)]">Expurgo local</div>
            <div className="text-2xl font-mono font-semibold">{retention.purged || 0}</div>
          </div>
          <button className="sbc-btn sbc-btn-danger" onClick={runRetention} disabled={busy} title="Processar agora">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="sbc-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <HardDriveUpload size={16} className="text-[color:var(--accent-blue)]" />
            <h2 className="text-sm font-semibold">Destino externo</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-xs">
              Protocolo
              <select className="sbc-input w-full mt-1" value={form.protocol} onChange={(e) => protocol(e.target.value)}>
                <option value="sftp">SFTP (recomendado)</option>
                <option value="ftp">FTP</option>
              </select>
            </label>
            <label className="text-xs">
              Servidor
              <input className="sbc-input w-full mt-1" value={form.host} onChange={(e) => change("host", e.target.value)} placeholder="storage.empresa.com" />
            </label>
            <label className="text-xs">
              Porta
              <input className="sbc-input w-full mt-1" type="number" value={form.port} onChange={(e) => change("port", Number(e.target.value))} />
            </label>
            <label className="text-xs">
              Usuário
              <input className="sbc-input w-full mt-1" value={form.username} onChange={(e) => change("username", e.target.value)} />
            </label>
            <label className="text-xs">
              Senha
              <input
                className="sbc-input w-full mt-1"
                type="password"
                value={form.password}
                onChange={(e) => change("password", e.target.value)}
                placeholder={form.password_configured ? "Senha já configurada" : "Informe a senha"}
              />
            </label>
            <label className="text-xs">
              Pasta remota
              <input className="sbc-input w-full mt-1 font-mono" value={form.remote_dir} onChange={(e) => change("remote_dir", e.target.value)} />
            </label>
            <label className="text-xs">
              Manter cópia local por dias
              <input
                className="sbc-input w-full mt-1"
                type="number"
                min="0"
                max="365"
                value={form.keep_local_days}
                onChange={(e) => change("keep_local_days", Number(e.target.value))}
              />
            </label>
            <label className="text-xs">
              Manter cópia externa por dias
              <input
                className="sbc-input w-full mt-1"
                type="number"
                min="0"
                max="3650"
                value={form.keep_remote_days}
                onChange={(e) => change("keep_remote_days", Number(e.target.value))}
              />
            </label>
            <label className="flex items-center gap-2 text-xs self-end h-9">
              <input type="checkbox" checked={form.enabled} onChange={(e) => change("enabled", e.target.checked)} />
              Ativar envio externo
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="sbc-btn" onClick={test} disabled={busy}>
              <PlugZap size={14} />
              Testar conexão
            </button>
            <button className="sbc-btn sbc-btn-primary" onClick={save} disabled={busy}>
              <Save size={14} />
              {busy ? "Processando..." : "Salvar"}
            </button>
          </div>
        </div>

        <div className="sbc-card p-4 space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-[color:var(--accent-amber)]" />
            <h2 className="text-sm font-semibold">Verificação por cliente</h2>
          </div>

          <div className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Último envio com sucesso</div>
            <div className="text-sm font-medium">{retention.last_sent?.filename || "Nenhum envio confirmado ainda"}</div>
            <div className="text-xs text-[color:var(--text-secondary)] mt-1">{formatDateTime(retention.last_sent?.updated_at)}</div>
            {retention.last_sent?.remote_path && (
              <div className="text-[11px] font-mono text-[color:var(--text-secondary)] mt-2 break-all">{retention.last_sent.remote_path}</div>
            )}
          </div>

          <div className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Última falha</div>
            <div className="text-sm font-medium">{retention.last_error?.filename || "Sem falhas recentes"}</div>
            <div className="text-xs text-[color:var(--text-secondary)] mt-1">{formatDateTime(retention.last_error?.updated_at)}</div>
            {retention.last_error?.error && (
              <div className="text-[11px] text-[color:var(--accent-red)] mt-2 break-words">{retention.last_error.error}</div>
            )}
          </div>
        </div>
      </div>

      <div className="sbc-card overflow-hidden">
        <table className="sbc-table">
          <thead>
            <tr>
              <th>Origem</th>
              <th>Identificador</th>
              <th>Arquivo</th>
              <th>Destino</th>
              <th>Status</th>
              <th>Atualizado</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.source_type || "-"}</td>
                  <td className="font-mono">{row.source_id || "-"}</td>
                  <td className="font-mono text-xs">{row.filename}</td>
                  <td className="font-mono text-xs max-w-[320px] truncate">{row.remote_path || "-"}</td>
                  <td>
                    <StatusTag status={row.status} title={row.error} />
                  </td>
                  <td>{formatDateTime(row.updated_at)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center text-[color:var(--text-muted)]">
                  Nenhuma gravação processada para este cliente.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
